import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../components/ui/select";
import { HowItWorksButton } from "./HowItWorks";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const SERVICE_OPTIONS = [
  "Voice Over Narration",
  "Voice Acting",
  "Audio Production, Editing & Mastering",
  "Social Media Promos & Book Teasers",
];

const BUDGET_OPTIONS = [
  { value: "Under $100", desc: "Perfect for quick social media promos or single-track mastering" },
  { value: "$100 – $250", desc: "Ideal for short commercial spots, podcast episodes, or small vocal sets" },
  { value: "$250 – $500", desc: "Great for multi-track music mixing or indie game character roles" },
  { value: "$500 – $1,250", desc: "Standard tier for short audiobooks or full trailer sound design" },
  { value: "$1,250+", desc: "Full-length novels, music albums, or large-scale campaigns" },
];

export const QuoteDialog = ({ trigger, defaultService = "", serviceValue = "", listenGlobal = false }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const initMessage = defaultService ? `Regarding: ${defaultService}\n\n` : "";
  const initService = SERVICE_OPTIONS.includes(serviceValue) ? serviceValue : "";
  const blank = { name: "", email: "", company: "", service: initService, budget: "", message: initMessage };
  const [form, setForm] = useState(blank);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  React.useEffect(() => {
    if (!listenGlobal) return;
    const handler = (e) => {
      const sv = e.detail?.service;
      setForm({ name: "", email: "", company: "", service: SERVICE_OPTIONS.includes(sv) ? sv : "", budget: "", message: "" });
      setOpen(true);
    };
    window.addEventListener("eovo:open-quote", handler);
    return () => window.removeEventListener("eovo:open-quote", handler);
  }, [listenGlobal]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Request sent! Evan will reply within 24 hours.");
      setOpen(false);
      setForm(blank);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="bg-[#12141A] border-white/10 text-white max-w-lg" data-testid="quote-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="font-head text-2xl">Request a Quote</DialogTitle>
            <HowItWorksButton testId="hiw-btn-quote" />
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#A1A1AA]">Name *</Label>
              <Input required value={form.name} onChange={set("name")} data-testid="quote-name" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Email *</Label>
              <Input required type="email" value={form.email} onChange={set("email")} data-testid="quote-email" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#A1A1AA]">Company</Label>
              <Input value={form.company} onChange={set("company")} data-testid="quote-company" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Budget</Label>
              <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                <SelectTrigger data-testid="quote-budget" className="bg-[#0B0C10] border-white/10 mt-1 text-left">
                  <span className={form.budget ? "text-white" : "text-[#6b6b73]"}>{form.budget || "Select a range"}</span>
                </SelectTrigger>
                <SelectContent className="bg-[#12141A] border-white/10 text-white max-w-[90vw]">
                  {BUDGET_OPTIONS.map((b) => (
                    <SelectItem key={b.value} value={b.value} data-testid={`budget-opt-${b.value}`} className="focus:bg-[#C8A97E]/15 focus:text-white">
                      <div className="flex flex-col py-0.5">
                        <span className="font-semibold text-sm">{b.value}</span>
                        <span className="text-xs text-[#8b8b93] leading-snug">{b.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs text-[#A1A1AA]">Service</Label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger data-testid="quote-service" className="bg-[#0B0C10] border-white/10 mt-1 text-left">
                <span className={form.service ? "text-white" : "text-[#6b6b73]"}>{form.service || "Select a service"}</span>
              </SelectTrigger>
              <SelectContent className="bg-[#12141A] border-white/10 text-white max-w-[90vw]">
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`service-opt-${s}`} className="focus:bg-[#C8A97E]/15 focus:text-white">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-[#A1A1AA]">Project details *</Label>
            <Textarea required value={form.message} onChange={set("message")} rows={4} data-testid="quote-message" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
          <Button type="submit" disabled={sending} data-testid="quote-submit"
            className="w-full bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">
            {sending ? "Sending..." : "Send Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
